import { FeedFollowBehavior, isAdminLayout, PaginationInfo } from '../shared';
import { graphqlApiWithPersisted } from './graphqlApi';
import { storyCardQuery } from './helpers';
import { InAppMessageContent } from './inAppMessage';
import { StoryCard, StoryCardStatus, StoryCardType } from './storyCard';

export enum NewsFeedType {
  CHAT_PROMOTIONS = 'CHAT_PROMOTIONS',
  HORIZONTAL_STORY_FEED = 'HORIZONTAL_STORY_FEED',
  IN_APP_MESSAGE = 'IN_APP_MESSAGE',
  STORY_CARD_ITEM = 'STORY_CARD_ITEM',
}

export enum NewsFeedScope {
  WEB_FEED = 'WEB_FEED',
  MOBILE_FEED = 'MOBILE_FEED',
}

export interface HorizontalStoryFeedData {
  pageInfo: PaginationInfo;
  storyId: number;
  items: StoryCard[];
}

export interface StoryCardItemData {
  item: StoryCard;
}

export interface NewsFeedInAppMessageData extends InAppMessageContent {
  image?: {
    url?: string;
    width?: number;
  } | null;
}

export interface NewsFeedItem {
  type: NewsFeedType;
  data: HorizontalStoryFeedData | NewsFeedInAppMessageData | StoryCardItemData;
}

interface NewsFeedData {
  items: NewsFeedItem[];
  pageInfo: PaginationInfo;
}

interface NewsFeedResponse {
  data: { newsfeedComposed: NewsFeedData };
  errors?: [{ message: string }];
}

interface NewsFeedPersonalizedResponse {
  data: { newsfeedComposedPersonalized: NewsFeedData };
  errors?: [{ message: string }];
}

export interface NewsFeedFilter {
  query?: string;
  from?: string;
  to?: string;
  onlyCompletedMedia?: boolean;
  feedType?: NewsFeedType[];
  scope?: NewsFeedScope;
  storyCardType?: StoryCardType[];
  authorId?: number[];
  categoryId: number[];
  sourceName?: (string | null)[];
  organisationTags?: {
    externalTagsId: (number | null)[];
    externalStatusTagsId?: (number | null)[];
    internalTagsId: (number | null)[];
  };
  storyId?: number[];
  status?: StoryCardStatus[];
  feedFollowBehavior?: FeedFollowBehavior[];
}

export interface NewsFeedArgs {
  channelId: number;
  page?: number;
  size?: number;
  filter?: NewsFeedFilter;
}

export interface NewsFeedPersonalizedArgs {
  channelId: number;
  page?: number;
  size?: number;
}

export const newsFeedApi = graphqlApiWithPersisted.injectEndpoints({
  endpoints: (builder) => ({
    newsFeed: builder.query<NewsFeedData, NewsFeedArgs>({
      query: ({ channelId, page = 1, size = 10, filter }) => ({
        url: '/graphql/webapp?newsfeedComposed',
        method: 'POST',
        body: {
          query: `query NewsfeedComposed($channelId: Int!, $page: Int, $size: Int, $filter: GetNewsfeedComposeFilterArgs) {
            newsfeedComposed(channelId: $channelId, page: $page, size: $size, filter: $filter) {
              items {
                ... on NewsfeedComposeElementHorizontalStoryFeed {
                  type
                  data {
                    pageInfo {
                      page
                      perPage
                      hasNextPage
                    }
                    storyId
                    items {
                      ${storyCardQuery({ isFeed: true, isAdminLayout: isAdminLayout() })}
                    }
                  }     
                }
                ... on NewsfeedComposeElementStoryCardItem {
                  type
                  data {
                    item {
                      ${storyCardQuery({ isFeed: true, isAdminLayout: isAdminLayout() })}
                    }
                  }
                }
                ... on NewsfeedComposeElementInAppMessage {
                  type
                  data {
                    id
                    title
                    intro
                    bgColor
                    textColor
                    image {
                      url
                      width
                    }
                    primary {
                      text
                      textColor
                      bgColor
                      action
                    }
                  }
                }
              }
              pageInfo {
                page
                hasNextPage
              }
            }
          }`,
          variables: {
            channelId,
            page,
            size,
            filter,
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: NewsFeedResponse) => {
        if (response?.errors?.length) {
          return {
            items: [],
            pageInfo: { page: 0, hasNextPage: false } as PaginationInfo,
          };
        }

        return response.data.newsfeedComposed;
      },
    }),
    newsFeedPersonalized: builder.query<NewsFeedData, NewsFeedPersonalizedArgs>({
      query: ({ channelId, page = 1, size = 10 }) => ({
        url: '/graphql/webapp?newsfeedComposedPersonalized',
        method: 'POST',
        body: {
          query: `query NewsfeedComposedPersonalized($channelId: Int!, $page: Int, $size: Int) {
            newsfeedComposedPersonalized(channelId: $channelId, page: $page, size: $size) {
              items {
                ... on NewsfeedComposeElementHorizontalStoryFeed {
                  type
                  data {
                    pageInfo {
                      page
                      perPage
                      hasNextPage
                    }
                    storyId
                    items {
                      ${storyCardQuery({ isFeed: true, isAdminLayout: isAdminLayout() })}
                    }
                  }
                }
                ... on NewsfeedComposeElementStoryCardItem {
                  type
                  data {
                    item {
                      ${storyCardQuery({ isFeed: true, isAdminLayout: isAdminLayout() })}
                    }
                  }
                }
                ... on NewsfeedComposeElementInAppMessage {
                  type
                  data {
                    id
                    title
                    intro
                    bgColor
                    textColor
                    image {
                      url
                      width
                    }
                    primary {
                      text
                      textColor
                      bgColor
                      action
                    }
                  }
                }
              }
              pageInfo {
                page
                hasNextPage
              }
            }
          }`,
          variables: {
            channelId,
            page,
            size,
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: NewsFeedPersonalizedResponse) => {
        if (response?.errors?.length) {
          return {
            items: [],
            pageInfo: { page: 0, hasNextPage: false } as PaginationInfo,
          };
        }

        return response.data.newsfeedComposedPersonalized;
      },
    }),
  }),
});

export const { useNewsFeedQuery } = newsFeedApi;
